import { BlogArticle } from '../types';

// 間隔重複記憶法 → CTA: /module/recite
const article: BlogArticle = {
  slug: 'spaced-repetition-flashcards-guide',
  date: '2026-09-15',
  readingMinutes: 4,
  tags: ['字卡', '記憶法', '溫書技巧'],

  locales: {
    zhHant: {
      title: '間隔重複記憶法：字卡要「咁樣用」先有效',
      excerpt: '點解你背完轉頭就唔記得？因為你喺錯嘅時間溫錯嘅嘢。間隔重複（Spaced Repetition）係科學證實最有效嘅記憶方法。',
      keywords: '間隔重複, 字卡, flashcards, 記憶法, 遺忘曲線',
      contentHtml: `
        <p>背書背到第 10 頁，第 1 頁已經唔記得晒？問題唔係你嘅記性，而係溫習嘅<strong>時機</strong>。德國心理學家 Ebbinghaus 嘅遺忘曲線話我哋知：學完新嘢之後，記憶會急速衰退 —— 但如果喺啱嘅時間點複習，就可以將遺忘曲線「重置」。</p>

        <h2>咩係間隔重複？</h2>
        <p>好簡單：<strong>喺你差唔多要唔記得嘅時候，先複習</strong>。每次成功回憶，下次複習嘅間隔就可以拉長：</p>
        <ol>
          <li>第 1 次複習：學完後 <strong>1 日</strong></li>
          <li>第 2 次：<strong>3 日</strong>後</li>
          <li>第 3 次：<strong>7 日</strong>後</li>
          <li>第 4 次：<strong>14 日</strong>後</li>
          <li>之後：<strong>30 日</strong>後</li>
        </ol>

        <div class="tip-box">
          <strong>💡 貼士：</strong>複習嗰陣一定要「主動回憶」—— 遮住答案，逼自己諗返出嚟。齋睇答案係冇用嘅。
        </div>

        <h2>點樣整一張好字卡？</h2>
        <ul>
          <li><strong>一卡一概念</strong>：唔好將成段嘢塞入一張卡</li>
          <li><strong>問題形式</strong>：正面寫問題（「photosynthesis 嘅產物係咩？」），唔好齋寫個詞</li>
          <li><strong>加例子</strong>：抽象概念配一個具體例子，記憶會深刻好多</li>
        </ul>

        <h2>常見錯誤</h2>
        <table>
          <thead>
            <tr><th>❌ 做法</th><th>✅ 更好做法</th></tr>
          </thead>
          <tbody>
            <tr><td>考試前一晚狂背</td><td>分散喺幾星期複習</td></tr>
            <tr><td>順住次序背</td><td>隨機抽卡測驗自己</td></tr>
            <tr><td>唔識嘅卡重複睇</td><td>唔識嘅卡縮短間隔再考</td></tr>
          </tbody>
        </table>

        <h2>工具幫到手</h2>
        <p>手整字卡太花時間？上載你嘅筆記或者輸入課題，AI 會自動幫你生成字卡，仲可以隨機抽問：</p>
        <p><a class="blog-cta-button" href="/module/recite">📚 免費生成智能字卡 →</a></p>

        <blockquote>記憶唔係靠死背，係靠喺啱嘅時間複習啱嘅嘢。</blockquote>
      `,
    },

    zhHans: {
      title: '间隔重复记忆法：字卡要「这样用」才有效',
      excerpt: '为什么你背完转头就忘？因为你在错的时间复习错的内容。间隔重复（Spaced Repetition）是科学证实最有效的记忆方法。',
      keywords: '间隔重复, 字卡, flashcards, 记忆法, 遗忘曲线',
      contentHtml: `
        <p>背书背到第 10 页，第 1 页已经忘光了？问题不是你的记性，而是复习的<strong>时机</strong>。德国心理学家 Ebbinghaus 的遗忘曲线告诉我们：学完新东西后，记忆会急速衰退 —— 但如果在对的时间点复习，就能把遗忘曲线「重置」。</p>

        <h2>什么是间隔重复？</h2>
        <p>很简单：<strong>在你差不多要忘记的时候，才复习</strong>。每次成功回忆，下次复习的间隔就可以拉长：</p>
        <ol>
          <li>第 1 次复习：学完后 <strong>1 天</strong></li>
          <li>第 2 次：<strong>3 天</strong>后</li>
          <li>第 3 次：<strong>7 天</strong>后</li>
          <li>第 4 次：<strong>14 天</strong>后</li>
          <li>之后：<strong>30 天</strong>后</li>
        </ol>

        <div class="tip-box">
          <strong>💡 提示：</strong>复习时一定要「主动回忆」—— 遮住答案，逼自己想出来。只看答案是没用的。
        </div>

        <h2>怎样做一张好字卡？</h2>
        <ul>
          <li><strong>一卡一概念</strong>：不要把整段内容塞进一张卡</li>
          <li><strong>问题形式</strong>：正面写问题（「光合作用的产物是什么？」），不要只写个词</li>
          <li><strong>加例子</strong>：抽象概念配一个具体例子，记忆会深刻很多</li>
        </ul>

        <h2>常见错误</h2>
        <table>
          <thead>
            <tr><th>❌ 做法</th><th>✅ 更好做法</th></tr>
          </thead>
          <tbody>
            <tr><td>考试前一晚狂背</td><td>分散在几星期复习</td></tr>
            <tr><td>顺着次序背</td><td>随机抽卡测验自己</td></tr>
            <tr><td>不会的卡重复看</td><td>不会的卡缩短间隔再考</td></tr>
          </tbody>
        </table>

        <h2>工具帮到你</h2>
        <p>手做字卡太花时间？上传你的笔记或输入课题，AI 会自动帮你生成字卡，还可以随机抽问：</p>
        <p><a class="blog-cta-button" href="/module/recite">📚 免费生成智能字卡 →</a></p>

        <blockquote>记忆不是靠死背，是靠在对的时间复习对的内容。</blockquote>
      `,
    },

    en: {
      title: 'Spaced Repetition: How to Use Flashcards the Right Way',
      excerpt: "Why do you forget everything right after memorising it? You're reviewing at the wrong time. Spaced repetition is the most science-backed memory method there is.",
      keywords: 'spaced repetition, flashcards, memory techniques, forgetting curve, study methods',
      contentHtml: `
        <p>Get to page 10 and realise page 1 is already gone? The problem isn't your memory — it's <strong>when</strong> you review. Ebbinghaus' forgetting curve shows that memory decays fast after learning — but reviewing at the right moment "resets" the curve.</p>

        <h2>What is spaced repetition?</h2>
        <p>Simple: <strong>review right before you're about to forget</strong>. Every successful recall lets you stretch the next interval:</p>
        <ol>
          <li>First review: <strong>1 day</strong> after learning</li>
          <li>Second: <strong>3 days</strong> later</li>
          <li>Third: <strong>7 days</strong> later</li>
          <li>Fourth: <strong>14 days</strong> later</li>
          <li>Then: <strong>30 days</strong> later</li>
        </ol>

        <div class="tip-box">
          <strong>💡 Tip:</strong> Reviews must use active recall — cover the answer and force yourself to retrieve it. Passively reading answers does nothing.
        </div>

        <h2>What makes a good flashcard?</h2>
        <ul>
          <li><strong>One card, one concept</strong> — never cram a whole paragraph into a card</li>
          <li><strong>Phrase it as a question</strong> ("What are the products of photosynthesis?"), not just a term</li>
          <li><strong>Add an example</strong> — abstract ideas stick when paired with something concrete</li>
        </ul>

        <h2>Common mistakes</h2>
        <table>
          <thead>
            <tr><th>❌ Habit</th><th>✅ Better</th></tr>
          </thead>
          <tbody>
            <tr><td>Cramming the night before</td><td>Spreading reviews over weeks</td></tr>
            <tr><td>Memorising in order</td><td>Testing yourself with shuffled cards</td></tr>
            <tr><td>Re-reading failed cards</td><td>Re-testing failed cards sooner</td></tr>
          </tbody>
        </table>

        <h2>Let tools do the heavy lifting</h2>
        <p>Making cards by hand takes forever. Upload your notes or enter a topic and let AI generate flashcards for you — it can even quiz you in random order:</p>
        <p><a class="blog-cta-button" href="/module/recite">📚 Generate smart flashcards for free →</a></p>

        <blockquote>Memory isn't about brute force — it's about reviewing the right thing at the right time.</blockquote>
      `,
    },
  },
};

export default article;
